/**
 * Sample Text Provider
 *
 * Fetches demo content from Project Gutenberg with:
 * - localStorage caching with timestamp
 * - Embedded fallback excerpt for offline use
 *
 * Source: "Elementary Principles of Statistical Mechanics" by J. Willard Gibbs
 * Chapter IV - On the Distribution in Phase called Canonical
 */

/**
 * localStorage keys
 */
const STORAGE_KEYS = {
  CACHED_TEXT: 'rsvp-xr-sample-text',
  CACHE_TIMESTAMP: 'rsvp-xr-sample-text-timestamp',
} as const;

/**
 * Cache duration: 24 hours in milliseconds
 */
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;

/**
 * Project Gutenberg URL for the book
 * Using the plain text version for easier parsing
 */
const GUTENBERG_URL =
  'https://www.gutenberg.org/cache/epub/50992/pg50992.txt';

/**
 * Fallback excerpt - used when network fetch fails
 * This is a condensed excerpt from Chapter IV to ensure the demo always works
 */
const FALLBACK_EXCERPT = `Let us imagine a great number of independent systems, identical in nature, but differing in phase, that is, in their condition with respect to configuration and velocity. The forces are supposed to be determined for every system by the same law, and to be conservative. The systems are supposed to be isolated, except that they may share energy with a great reservoir of heat.

We shall first consider a case in which the energy is the only first integral of the equations of motion. In this case the probability that any system shall have an energy less than a given value may be expressed as a function of that energy.

The average value of any quantity determined by the phase may be found by integrating that quantity, multiplied by the probability of the phase, over all possible phases. This integral, divided by the integral of the probability of the phase over all phases, will give the average value of the quantity.

The most important average values are those of the energy and its functions. The average kinetic energy of the system is proportional to the temperature, a fundamental result connecting mechanics with thermodynamics.

When a system is in contact with a great heat reservoir at a definite temperature, its energy fluctuates about a mean value. The average of these fluctuations vanishes, but the average of their squares does not vanish. This dispersion of energy is related to the heat capacity of the system.

The canonical distribution thus provides a complete statistical description of a system in thermal equilibrium. It forms the foundation for understanding how microscopic mechanical systems give rise to the macroscopic phenomena described by thermodynamics.`;

/**
 * Chapter IV markers for extraction
 */
const CHAPTER_START_MARKER = 'CHAPTER IV';
const CHAPTER_END_MARKER = 'CHAPTER V';

/**
 * Get sample text - tries cache first, then fetches, falls back to embedded excerpt
 */
export async function getSampleText(): Promise<string> {
  // Try cache first
  const cached = getCachedText();
  if (cached) {
    return cached;
  }

  // Try to fetch from Project Gutenberg
  try {
    const fetched = await fetchAndParseGutenberg();
    if (fetched) {
      cacheText(fetched);
      return fetched;
    }
  } catch (error) {
    console.warn('Failed to fetch sample text from Project Gutenberg:', error);
  }

  // Fall back to embedded excerpt
  return FALLBACK_EXCERPT;
}

/**
 * Get cached text if valid
 */
function getCachedText(): string | null {
  try {
    const timestamp = localStorage.getItem(STORAGE_KEYS.CACHE_TIMESTAMP);
    if (!timestamp) return null;

    const cacheTime = parseInt(timestamp, 10);
    const now = Date.now();

    // Check if cache is still valid
    if (now - cacheTime > CACHE_DURATION_MS) {
      // Cache expired, clear it
      localStorage.removeItem(STORAGE_KEYS.CACHED_TEXT);
      localStorage.removeItem(STORAGE_KEYS.CACHE_TIMESTAMP);
      return null;
    }

    const text = localStorage.getItem(STORAGE_KEYS.CACHED_TEXT);
    return text || null;
  } catch {
    // localStorage not available
    return null;
  }
}

/**
 * Cache text with timestamp
 */
function cacheText(text: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.CACHED_TEXT, text);
    localStorage.setItem(STORAGE_KEYS.CACHE_TIMESTAMP, String(Date.now()));
  } catch {
    // localStorage not available or quota exceeded
  }
}

/**
 * Fetch and parse Chapter IV from Project Gutenberg
 */
async function fetchAndParseGutenberg(): Promise<string | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  try {
    const response = await fetch(GUTENBERG_URL, {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const fullText = await response.text();
    return extractChapterIV(fullText);
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

/**
 * Extract Chapter IV content from the full text
 */
function extractChapterIV(fullText: string): string | null {
  // Find Chapter IV start
  const startIndex = fullText.indexOf(CHAPTER_START_MARKER);
  if (startIndex === -1) {
    console.warn('Chapter IV marker not found');
    return null;
  }

  // Find Chapter V start (end of Chapter IV)
  const endIndex = fullText.indexOf(CHAPTER_END_MARKER, startIndex + CHAPTER_START_MARKER.length);

  // Extract the chapter content
  const chapterContent = endIndex !== -1
    ? fullText.slice(startIndex, endIndex)
    : fullText.slice(startIndex);

  // Clean up the text
  const cleaned = cleanText(chapterContent);

  // Validate we got meaningful content
  if (cleaned.length < 500) {
    console.warn('Extracted chapter too short');
    return null;
  }

  return cleaned;
}

/**
 * Clean up extracted text
 */
function cleanText(text: string): string {
  return text
    // Normalize line endings
    .replace(/\r\n/g, '\n')
    // Remove the chapter title line itself for cleaner reading
    .replace(/^CHAPTER IV[^\n]*\n+/i, '')
    // Collapse multiple newlines to single space (paragraph breaks become spaces)
    .replace(/\n\n+/g, ' ')
    // Replace single newlines with space (wrapped lines)
    .replace(/\n/g, ' ')
    // Collapse multiple spaces
    .replace(/\s+/g, ' ')
    // Trim
    .trim();
}

/**
 * Get the fallback excerpt directly (for testing or immediate use)
 */
export function getFallbackExcerpt(): string {
  return FALLBACK_EXCERPT;
}

/**
 * Clear the cached sample text (for testing)
 */
export function clearSampleTextCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.CACHED_TEXT);
    localStorage.removeItem(STORAGE_KEYS.CACHE_TIMESTAMP);
  } catch {
    // localStorage not available
  }
}
